import React from 'react';
import { FiFileText, FiBook, FiUsers, FiMail, FiShare2, FiCreditCard } from 'react-icons/fi';

export function ContentFormatStep({ formData, onChange, errors }) {
  const contentFormats = [
    {
      value: 'blog-post',
      label: 'Blog Post',
      icon: FiFileText,
      description: '800-1500 words with SEO optimization',
      details: 'Perfect for thought leadership and driving organic traffic',
      timeToCreate: '2-4 minutes',
      audience: 'Broad audience, SEO-focused'
    },
    {
      value: 'white-paper',
      label: 'White Paper',
      icon: FiBook,
      description: 'Comprehensive technical deep-dive (2000+ words)',
      details: 'Authoritative content for lead generation and nurturing',
      timeToCreate: '5-8 minutes',
      audience: 'Technical buyers, decision makers'
    },
    {
      value: 'case-study',
      label: 'Case Study',
      icon: FiUsers,
      description: 'Success story with metrics and outcomes',
      details: 'Builds credibility through real customer examples',
      timeToCreate: '2-3 minutes',
      audience: 'Prospects evaluating solutions'
    },
    {
      value: 'email-campaign',
      label: 'Email Campaign',
      icon: FiMail,
      description: 'Multi-touch nurture sequence (3-5 emails)',
      details: 'Automated follow-up with personalized messaging',
      timeToCreate: '3-4 minutes',
      audience: 'Qualified leads and prospects'
    },
    {
      value: 'linkedin-posts',
      label: 'LinkedIn Posts',
      icon: FiShare2,
      description: 'Professional LinkedIn posts with industry insights',
      details: 'Longer-form social content for professional engagement',
      timeToCreate: '1-2 minutes',
      audience: 'Professional network, B2B decision makers'
    },
    {
      value: 'x-twitter-posts',
      label: 'X (Twitter) Posts',
      icon: FiShare2,
      description: 'Short, punchy posts for quick engagement',
      details: 'Concise messaging optimized for X platform',
      timeToCreate: '1-2 minutes',
      audience: 'Broader audience, quick engagement'
    },
    {
      value: 'sales-enablement',
      label: 'Sales Enablement',
      icon: FiCreditCard,
      description: 'One-pagers, battle cards, and pitch materials',
      details: 'Tools for sales team to close deals effectively',
      timeToCreate: '3-5 minutes',
      audience: 'Sales team and direct prospects'
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Content Format Selection</h2>
        <p className="text-gray-600 mb-8">
          Choose the type of marketing content you want to create. Each format serves different purposes and audiences in your marketing funnel.
        </p>
      </div>

      {errors.contentFormat && (
        <div className="mb-4">
          <p className="text-red-600 text-sm">{errors.contentFormat}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {contentFormats.map((format) => {
          const IconComponent = format.icon;
          return (
            <div
              key={format.value}
              className={`relative rounded-lg border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
                formData.contentFormat === format.value
                  ? 'border-green-500 bg-green-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('contentFormat', format.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="contentFormat"
                  value={format.value}
                  checked={formData.contentFormat === format.value}
                  onChange={() => onChange('contentFormat', format.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-4 flex-1">
                  <div className="flex items-center mb-3">
                    <IconComponent className={`w-6 h-6 mr-3 ${
                      formData.contentFormat === format.value ? 'text-green-600' : 'text-gray-400'
                    }`} />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {format.label}
                    </h3>
                  </div>
                  
                  <p className="text-gray-600 mb-3 font-medium">{format.description}</p>
                  <p className="text-sm text-gray-500 mb-4">{format.details}</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Est. creation time:</span>
                      <span className="font-medium text-gray-900">{format.timeToCreate}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Target audience:</span>
                      <span className="font-medium text-gray-900">{format.audience}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {formData.contentFormat && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Selected Format</h4>
          <p className="text-sm text-blue-700">
            You've chosen to create <strong>{contentFormats.find(f => f.value === formData.contentFormat)?.label}</strong> content. 
            This format typically takes {contentFormats.find(f => f.value === formData.contentFormat)?.timeToCreate} to generate 
            and is best suited for {contentFormats.find(f => f.value === formData.contentFormat)?.audience.toLowerCase()}.
          </p>
        </div>
      )}

      {/* Format-specific guidance */}
      {formData.contentFormat === 'blog-post' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">Blog Post - Marketing Style Guidelines</h4>
          <p className="text-sm text-yellow-700 space-y-1">
            Content will be written in engaging, narrative style with compelling storytelling. Expect flowing paragraphs that build emotional connection, strategic use of keywords for SEO, and persuasive calls-to-action that drive engagement. Includes professional hero image and supporting visuals from Unsplash.
          </p>
        </div>
      )}

      {formData.contentFormat === 'white-paper' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">White Paper - Marketing Style Guidelines</h4>
          <p className="text-sm text-yellow-700">
            Content will combine authoritative insights with persuasive marketing appeal. Expect compelling narrative structure that builds credibility through thought leadership while maintaining engagement and driving action toward TNS solutions. Includes professional data visualization and business imagery.
          </p>
        </div>
      )}

      {formData.contentFormat === 'case-study' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">Case Study - Marketing Style Guidelines</h4>
          <p className="text-sm text-yellow-700">
            Content will tell a compelling transformation story using narrative structure. Expect emotional storytelling that showcases customer success, quantifiable results, and clear benefits that prospects can envision for their own business. Includes industry-specific imagery and success story visuals.
          </p>
        </div>
      )}

      {formData.contentFormat === 'email-campaign' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">Email Campaign - Marketing Style Guidelines</h4>
          <p className="text-sm text-yellow-700">
            Content will be written in persuasive, action-oriented style with compelling subject lines and engaging body copy. Expect personalized messaging that builds relationships, creates urgency, and drives specific responses at each touchpoint. Includes email-optimized imagery and visual elements.
          </p>
        </div>
      )}

      {formData.contentFormat === 'social-media' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">Social Media - Marketing Style Guidelines</h4>
          <p className="text-sm text-yellow-700">
            Content will be engaging, shareable, and optimized for professional networks. Expect compelling hooks, clear value propositions, and conversation-starting content that builds brand awareness and drives engagement. Includes social media-ready visuals and graphics.
          </p>
        </div>
      )}

      {formData.contentFormat === 'sales-enablement' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">Sales Enablement - Marketing Style Guidelines</h4>
          <p className="text-sm text-yellow-700">
            Content will be persuasive and sales-focused while maintaining marketing appeal. Expect compelling competitive positioning, clear value propositions, and objection-handling content that helps close deals effectively. Includes professional presentation imagery and supporting visuals.
          </p>
        </div>
      )}
    </div>
  );
}